/*
 * Shared middleware routing component
 * 
 * Purpose:  Provide a generic, configurable way to enforce authentication on every
 *           route change.
 * 
 * Parameters:
 *      redirectPaths:  Use this to perform route-level redirection.  For example, 
 *                      if you want to redirect anybody from the "/foo" page to the 
 *                      "/bar" page, send the following:
 *              
 *                      ex:  [{ path: '/foo', redirect: '/bar' }]
 * 
 *                      NOTE:  You can also do a custom redirect based on cookie values.
 *                      For example, on the Fund site, we always want the '/' route to redirect
 *                      to the last-visited partner fund site.  To achieve this, note 
 *                      the "cookie__" prefix on the redirect property.  See the example below:
 * 
 *                      ex: { path: '/', redirect: '/cookie__preferred-partner' }
 * 
 *      authNotRequiredRegexArray:  Use this to allow public pages on your app. 
 *                      IMPORTANT:  This uses nuxt page syntax, found in `context.route.name`.
 *                                  Example: "index___en".  This allows us to specify nuxt
 *                                  pages without having to worry about route parameters.
 * 
 *                      ex:  ['^index___..$', '^partner___..$']  <--- NOTE THIS IS REGEX
 *
 *      permissions:  Use this to lock down pages in your app, or the entire app. 
 *                    IMPORTANT:  the resolvedRoute property uses nuxt page syntax, found 
 *                                in `context.route.name`. Example: "index___en".
 *
 *      ex:  [
 *               {
 *               resolvedRoute: "all", // special case.  All routes will require this.
 *               requiredPermission: "app_ops"
 *               },
 *               {
 *               resolvedRoute: "relief-partner-edit",
 *               requiredPermission: "relief-partner-edit"
 *               },
 *               {
 *               resolvedRoute: "relief-fund-create",
 *               requiredPermission: "relief-fund-create"
 *               },
 *               {
 *               resolvedRoute: "manage-user",
 *               requiredPermission: "auth-admin"
 *               },
 *               {
 *               resolvedRoute: "groups",
 *               requiredPermission: "app_social"
 *               redirect: "/" // In case you want to override the /error?code=403 redirect with a custom route
 *               }
 *          ]
 * 
 *      the requiredPermission property blocks members who do not have a specific permission
 *    
 *      
 *      ex:  [
 *               {
 *               resolvedRoute: "all", // special case.  All routes will require this.
 *               requiredPermission: "landing_only"
 *               }
 *           ]
 * 
 * Impementation:
 * 
 * In your nuxt app, in middleware/filename.js:
 * 
  ```
   import customrouter from '@uptogether/auth-spa-vue-components/middlewareRouting'
 
   export default function(context) {
    return customrouter(context, [], [], [])
   }
 ```
*/
export default async function (
  context,
  redirectPaths = [],
  authNotRequiredRegexArray = [],
  permissions = []
) {
  // set the i18n and fiiAuth-locale cookie
  context.app.i18n.setLocaleCookie(context.app.i18n.locale)
  context.store.commit('fiiAuth/setLocale', context.app.i18n.locale)

  // update Intercom https://app.intercom.com/a/apps/drnlj5h7/settings/web
  // verify Intercom is enabled via the console
  if (window.Intercom) {
    window.Intercom('update')
  }

  let user = undefined
  const resolvedRoute = context.route.name
  const uptogetherRefreshCookie = context.app.$cookies.get('refresh._token.uptogether')

  let isWhitelisted = async (authNotRequiredRegexArray, context) => {
    /*
     *  handle white listed routes
     */
    authNotRequiredRegexArray = [...['^error___..$'], ...authNotRequiredRegexArray]
    for (let i = 0; i < authNotRequiredRegexArray.length; i++) {
      const regex = RegExp(authNotRequiredRegexArray[i])

      if (regex.test(context.route.name)) {
        console.log(
          'Whitelisted route detected, do not force auth. ',
          context.route.name,
          context.route.fullPath
        )

        if (!uptogetherRefreshCookie) {
          await context.store.dispatch('fiiAuth/logout')
        }
        console.log('returning...')
        return true
      } else {
        console.log('Whitelisted route not detected', context.route.name, context.route.fullPath)
      }
    }
    // Fallthru: route is not white listed
    return false
  }

  let authorize = (user, permissions, resolvedRoute) => {
    /*
     *  AUTHORIZATION BEGIN.  If permissions are supplied, use them
     *  to ensure the user has the appropriate role for the app, or
     *  for individual routes in the app.
     */
    // Get logged-in users' roles

    const roles = user && user.roles ? user.roles : []

    // Find required permissions for the current page
    if (permissions && permissions.length > 0) {
      const currentRouteRequirementsArray = permissions.filter(
        (x) =>
          x.resolvedRoute === 'all' || (resolvedRoute && resolvedRoute.includes(x.resolvedRoute))
      )

      // Ensure the logged in user has all the required
      // permissions.  Otherwise, 403 'em.
      // Make sure the 403 redirect path is directly accessible when updating the permissions
      // If not, whitelist it: authNotRequiredRegexArray = ['error.+']
      // Otherwise you will get an infinite loop and lock up the browser:
      // Error: Redirected when going from "/error" to "/error" via a navigation guard
      // Local testing: log currentRouteRequirement.requiredPermission
      // Local testing note: change /error to /temporarily-down below if there is no /error route
      for (let i = 0; i < currentRouteRequirementsArray.length; i++) {
        const currentRouteRequirement = currentRouteRequirementsArray[i]
        if (
          currentRouteRequirement.requiredPermission &&
          !roles.includes(currentRouteRequirement.requiredPermission)
        ) {
          console.log('Middleware UNAUTHORIZED', resolvedRoute, roles)
          console.log('context.redirect("/error?code=403")')
          context.redirect(currentRouteRequirement.redirect || '/error?code=403')
        }
      }
    }
  }

  if (context.isHMR) {
    console.log('isHMR detected, returning')
    return
  }

  console.log('******* SHARED MIDDLEWARE COMPONENT **********')
  console.log('context.route.fullPath', context.route.fullPath)
  console.log('context.route.name', context.route.name)

  /*
   *  special redirect pages.
   *  Note: only perform redirection if we weren't already redirected.
   */
  const wasRedirectedFromMiddleware = context.route.query && context.route.query.r
  if (!wasRedirectedFromMiddleware) {
    const specialRedirectPaths = [...[{ path: '/login', redirect: '/' }], ...redirectPaths]
    for (let i = 0; i < specialRedirectPaths.length; i++) {
      const element = specialRedirectPaths[i]

      // check for cookie usage.  Adjust path if needed.
      let cookieRedirectPath
      const cookieExistanceArray = element.redirect.split('cookie__')
      if (cookieExistanceArray.length > 0 && cookieExistanceArray[1]) {
        cookieRedirectPath = context.app.$cookies.get(cookieExistanceArray[1])
        if (!cookieRedirectPath) {
          cookieRedirectPath = `${element.path}?r=1`
        }
      }

      let redirectValue = cookieRedirectPath ? cookieRedirectPath : element.redirect
      redirectValue = redirectValue.toString()
      const prefix = redirectValue.slice(0, 1) === '/' ? '' : '/'
      redirectValue = `${prefix}${redirectValue}`

      if (context.route.path === element.path) {
        context.redirect(`${redirectValue}`)
      }
    }
  }

  let whiteListedPage = await isWhitelisted(authNotRequiredRegexArray, context)

  // User needs to get logged in.
  if (uptogetherRefreshCookie) {
    if (!context.store.state.fiiAuth.identity) {
      console.log('middlewareRouting: loginUsingExistingToken')
      user = await context.store.dispatch(
        'fiiAuth/loginUsingExistingToken',
        uptogetherRefreshCookie
      )
    } else {
      user = context.store.state.fiiAuth.identity
    }
  }

  if (user) {
    const isDemoUser = user.meta.demoToken
    if (isDemoUser) {
      const appLocale = context.app.i18n.locale
      const userLocale = user.meta.languagePreference.language

      if (userLocale && userLocale !== appLocale) {
        console.log("middlewareRouting: update user's language preference")
        context.store.commit('fiiAuth/setLocale', userLocale)
        context.app.i18n.setLocale(userLocale)
        return
      }
    }
  }

  if (!user && !whiteListedPage) {
    /*
     *  user is not logged in, and this isnt a whitelisted route.
     *  redirect user to the login page.
     */
    const loc = window.location.href
    const encodedLoc = encodeURIComponent(loc)
    console.log('middlewareRouting auth2: No refresh token detected.  Kick user to Login page.')
    context.redirect(`${process.env.AUTH_URL}?redirect_url=${encodedLoc}`)
  }

  if (whiteListedPage) {
    console.log('Page is whitelisted.  Returning without further authorization.')
    return
  } else {
    console.log('Page is not whitelisted:' + context.route.fullPath + context.route.name)
    authorize(user, permissions, resolvedRoute)
  }
}
